import React, { useState } from 'react'
import { Button, DatePicker, Select } from 'antd'
import dayjs from 'dayjs'
import { SlackOutlined } from '@ant-design/icons'
import {
  ApprovalReportAPI,
  ApprovalReportDateWiseAPI,
} from '../../AllAPI/AdminAPI'
import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import NotoFont from '../../Assets/Font/NotoSansDevanagari'
import axiosInstance from '../../Utils/axiosInstance'

const { Option } = Select

const ApprovalReport = () => {
  const selectedYear = Number(sessionStorage.getItem('year'))

  const [loading, setLoading] = useState(false)
  const [viewType, setViewType] = useState('excel')
  const [optionType, setOptionType] = useState('all')
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(dayjs())

  /* ---------------- FORMAT DATE ---------------- */

  const formatDate = (dateString) => {
    if (!dateString) return ''
    return dayjs(dateString).format('DD-MM-YYYY')
  }

  /* ---------------- FETCH DATA ---------------- */

  const fetchOrders = async () => {
    if (!optionType) return

    setLoading(true)

    try {
      let res

      if (optionType === 'all') {
        res = await axiosInstance.get(`${ApprovalReportAPI}/${selectedYear}`)
      }

      if (optionType === 'date') {
        if (!fromDate || !toDate) return

        res = await axiosInstance.post(ApprovalReportDateWiseAPI, {
          fromDate: fromDate.format('YYYY-MM-DD'),
          toDate: toDate.format('YYYY-MM-DD'),
        })
      }

      const result = Array.isArray(res?.data) ? res.data : []

      if (!result.length) {
        toast.info('No approval data found')
        return
      }

      if (viewType === 'excel') {
        generateExcel(result)
      } else {
        generatePDF(result)
      }
    } catch (err) {
      console.error('Approval report fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  /* ---------------- FLATTEN DATA ---------------- */

  const formatGroupedData = (data) => {
    const rows = []

    data.forEach((order) => {
      order.orders.forEach((item, index) => {
        rows.push({
          order_id: index === 0 ? order.order_id : '',
          date: index === 0 ? formatDate(order.date) : '',
          customer: index === 0 ? order.customer_name : '',
          contact: index === 0 ? order.contact : '',
          address: index === 0 ? order.address : '',
          product: item.product_name,
          size: item.size,
          approval_qty: item.approval_quantity,
          approval_rate: item.approval_rate,
          approval_total:
            Number(item.approval_quantity) * Number(item.approval_rate),
          gross_total: index === 0 ? order.gross_total : '',
        })
      })
    })

    return rows
  }

  /* ---------------- EXCEL ---------------- */

  // const generateExcel = (data) => {
  //   const rows = formatGroupedData(data)

  //   const totalQty = rows.reduce(
  //     (sum, row) => sum + Number(row.approval_qty || 0),
  //     0,
  //   )

  //   const totalAmount = rows.reduce(
  //     (sum, row) => sum + Number(row.approval_total || 0),
  //     0,
  //   )

  //   rows.push({
  //     order_id: '',
  //     date: '',
  //     customer: '',
  //     contact: '',
  //     address: '',
  //     product: '',
  //     size: 'Grand Total',
  //     approval_qty: totalQty,
  //     approval_rate: '',
  //     approval_total: totalAmount,
  //     gross_total: '',
  //   })

  //   const worksheet = XLSX.utils.json_to_sheet(rows)
  //   const workbook = XLSX.utils.book_new()
  //   XLSX.utils.book_append_sheet(workbook, worksheet, 'Approval Report')

  //   const excelBuffer = XLSX.write(workbook, {
  //     bookType: 'xlsx',
  //     type: 'array',
  //   })

  //   const blob = new Blob([excelBuffer], {
  //     type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  //   })

  //   const url = URL.createObjectURL(blob)
  //   window.open(url)
  // }

  const generateExcel = (data, fileName = 'Approval_Report') => {
    const rows = formatGroupedData(data)

    //  Format Data
    const formattedData = rows.map((item) => ({
      'Order ID': item.order_id,
      'Approval Date': item.date,
      'Customer Name': item.customer,
      Contact: item.contact,
      Address: item.address,
      Product: item.product,
      Size: item.size,
      Quantity: item.approval_qty,
      Rate: item.approval_rate,
      'Total Amt': item.approval_total,
      'Gross Total': item.gross_total,
    }))

    //  Calculate Totals
    const totalQty = formattedData.reduce(
      (sum, item) => sum + Number(item.Quantity || 0),
      0,
    )

    const totalAmount = formattedData.reduce(
      (sum, item) => sum + Number(item['Total Amt'] || 0),
      0,
    )

    //  Add Grand Total Row
    formattedData.push({
      'Order ID': '',
      'Approval Date': '',
      'Customer Name': '',
      Contact: '',
      Address: '',
      Product: '',
      Size: 'Grand Total',
      Quantity: totalQty,
      Rate: '',
      'Total Amt': totalAmount,
      'Gross Total': '',
    })

    const worksheet = XLSX.utils.json_to_sheet(formattedData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Approval Report')

    //  Generate Current Date (DD-MM-YYYY)
    const today = new Date()
    const formattedDate = `${String(today.getDate()).padStart(2, '0')}-${String(
      today.getMonth() + 1,
    ).padStart(2, '0')}-${today.getFullYear()}`

    const finalFileName = `${fileName} (${formattedDate}).xlsx`

    //  Download File
    XLSX.writeFile(workbook, finalFileName)
  }

  /* ================= PDF (AutoTable) ================= */
  const generatePDF = (data) => {
    const doc = new jsPDF('landscape', 'mm', 'a4')

    // Marathi Font Support
    doc.addFileToVFS('NotoSansDevanagari.ttf', NotoFont)
    doc.addFont('NotoSansDevanagari.ttf', 'NotoSans', 'normal')
    doc.setFont('NotoSans')

    const rows = formatGroupedData(data)

    const totalQty = rows.reduce(
      (sum, row) => sum + Number(row.approval_qty || 0),
      0,
    )

    const totalAmount = rows.reduce(
      (sum, row) => sum + Number(row.approval_total || 0),
      0,
    )

    const tableColumns = [
      'Order ID',
      'Approval Date',
      'Customer Name',
      'Contact',
      'Address',
      'Product',
      'Size',
      'Approval Qty',
      'Approval Rate',
      'Approval Total',
      'Gross Total',
    ]

    const tableRows = rows.map((row) => [
      row.order_id,
      row.date,
      row.customer,
      row.contact,
      row.address,
      row.product,
      row.size,
      row.approval_qty,
      row.approval_rate,
      row.approval_total,
      row.gross_total,
    ])

    // Grand Total Row
    tableRows.push([
      '',
      '',
      '',
      '',
      '',
      '',
      'Grand Total',
      totalQty,
      '',
      totalAmount,
      '',
    ])

    doc.setFontSize(14)
    doc.text('APPROVAL REPORT', 14, 15)

    const today = new Date().toLocaleDateString('en-GB')

    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      startY: 22,
      theme: 'grid',

      //  Full width table
      tableWidth: 'auto',

      styles: {
        font: 'NotoSans',
        fontSize: 8,
        cellPadding: 2,
      },

      headStyles: {
        fillColor: [255, 102, 0],
      },

      margin: { left: 10, right: 10 },

      didDrawPage: function () {
        const pageCount = doc.internal.getNumberOfPages()
        const pageWidth = doc.internal.pageSize.getWidth()
        const pageHeight = doc.internal.pageSize.getHeight()

        doc.setFontSize(9)

        // Page number
        doc.text(
          `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
          pageWidth - 45,
          pageHeight - 8,
        )

        // Generated date
        doc.text(`(${today})`, pageWidth - 10, pageHeight - 8, {
          align: 'right',
        })
      },
    })

    const blob = doc.output('blob')
    window.open(URL.createObjectURL(blob), '_blank')
  }

  return (
    <div className="mainContainer">
      {loading && (
        <div className="fullscreen-loader">
          <div className="loader-box">
            <div className="spinner"></div>
            <p>Generating Report...</p>
          </div>
        </div>
      )}

      <div className="header-raw">
        <h3>
          <SlackOutlined style={{ color: 'orangered' }} /> Approval Report
        </h3>
      </div>

      <div className="row p-2 m-0 mb-2 rounded border shadow">
        <div className="col-md-2 pt-2">
          <label>View Type</label>
          <Select
            style={{ width: '100%' }}
            value={viewType}
            onChange={(value) => {
              setViewType(value)
              setOptionType('all')
            }}
          >
            <Option value="excel">Excel</Option>
            <Option value="pdf">PDF</Option>
          </Select>
        </div>

        <div className="col-md-2 pt-2">
          <label>Options</label>
          <Select
            style={{ width: '100%' }}
            value={optionType}
            onChange={(value) => setOptionType(value)}
          >
            <Option value="all">All</Option>
            <Option value="date">Date Wise</Option>
          </Select>
        </div>

        {optionType === 'date' && (
          <>
            <div className="col-md-2 pt-2">
              <label>From Date</label>
              <DatePicker
                value={fromDate}
                onChange={setFromDate}
                format="DD-MM-YYYY"
              />
            </div>

            <div className="col-md-2 pt-2">
              <label>To Date</label>
              <DatePicker
                value={toDate}
                onChange={setToDate}
                format="DD-MM-YYYY"
              />
            </div>
          </>
        )}

        <div className="col-md-2 pt-2 d-flex align-items-end">
          <Button
            type="primary"
            className="rounded"
            loading={loading}
            onClick={fetchOrders}
          >
            Show
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ApprovalReport
